import type { Comp, GameObj } from "kaplay";
import { config } from "../config";
import { LOOKS, type LookKey } from "../config/assetManifest";
import { CHUNK_H_M, CHUNK_W_M, GAME_H, PX_PER_M, TOUCH_ZOOM, ZOOM_MIN } from "../config/constants";
import type { DifficultyParams, EntityDescriptor } from "../config/types";
import { k } from "../core/kaplay";
import { spawnEntity } from "../core/registry";
import { viewport } from "../core/viewport";
import { generateChunk, type ChunkContext } from "../systems/chunkGen";
import type { Mountain } from "../systems/mountain";
import type { SurfaceRegion } from "../systems/surface";

const EDGE_MARGIN_M = 5;
const MOST_ZOOMED_OUT = ZOOM_MIN * TOUCH_ZOOM;

interface LoadedChunk {
    objects: GameObj[];
    regions: SurfaceRegion[];
}

export interface ChunkStreamerComp extends Comp {
    regions: SurfaceRegion[];
    loadedChunks(): number;
}

function regionOf(d: EntityDescriptor): SurfaceRegion | null {
    const obstacle = config.obstacles[d.type];
    if (obstacle?.kind !== "region" || !obstacle.surface) return null;
    const { w, h } = LOOKS[d.type as LookKey];
    const halfW = w / 2 / PX_PER_M;
    return { left: d.x - halfW, right: d.x + halfW, top: d.y - h / PX_PER_M, bottom: d.y, surface: obstacle.surface, gripBonus: obstacle.gripBonus };
}

function wantedChunks(): Set<string> {
    const cam = k.getCamPos().scale(1 / PX_PER_M);
    const halfW = viewport.width / MOST_ZOOMED_OUT / 2 / PX_PER_M + EDGE_MARGIN_M;
    const halfH = GAME_H / MOST_ZOOMED_OUT / 2 / PX_PER_M;
    const wanted = new Set<string>();
    for (let cx = Math.floor((cam.x - halfW) / CHUNK_W_M); cx <= Math.floor((cam.x + halfW) / CHUNK_W_M); cx++) {
        const firstRow = Math.floor((cam.y - halfH - EDGE_MARGIN_M) / CHUNK_H_M);
        const lastRow = Math.floor((cam.y + halfH) / CHUNK_H_M) + 1;
        for (let cy = firstRow; cy <= lastRow; cy++) wanted.add(`${cx},${cy}`);
    }
    return wanted;
}

function loadChunk(ctx: ChunkContext, key: string): LoadedChunk {
    const [cx, cy] = key.split(",").map(Number);
    const descriptors = generateChunk(ctx, cx, cy);
    return {
        objects: descriptors.map((d) => spawnEntity({ ...d, x: d.x * PX_PER_M, y: d.y * PX_PER_M })),
        regions: descriptors.map(regionOf).filter((r): r is SurfaceRegion => r !== null),
    };
}

function chunkStreamer(ctx: ChunkContext): ChunkStreamerComp {
    const loaded = new Map<string, LoadedChunk>();
    return {
        id: "chunkStreamer",
        regions: [],
        loadedChunks: () => loaded.size,

        update(this: ChunkStreamerComp) {
            const wanted = wantedChunks();
            let changed = false;
            for (const [key, chunk] of loaded) {
                if (wanted.has(key)) continue;
                chunk.objects.filter((obj) => !obj.is("roamer")).forEach((obj) => obj.destroy());
                loaded.delete(key);
                changed = true;
            }
            for (const key of wanted) {
                if (loaded.has(key)) continue;
                loaded.set(key, loadChunk(ctx, key));
                changed = true;
            }
            if (changed) this.regions = [...loaded.values()].flatMap((chunk) => chunk.regions);
        },
    };
}

export function addChunks(mountain: Mountain, courseEndM: number | null, difficultyAt: (yM: number) => DifficultyParams) {
    const ctx: ChunkContext = { mountain, zones: config.zones.zones, courseEndM, difficultyAt };
    return k.add([chunkStreamer(ctx)]);
}
